import { motion } from "framer-motion";
import { FaTimes, FaPlane, FaSuitcase, FaUtensils, FaInfoCircle, FaFileInvoiceDollar, FaClock, FaExchangeAlt } from "react-icons/fa";
import { useEffect } from "react";
import { useCurrency } from "../../context/CurrencyContext";
import { formatPriceSync } from "../../utils/currency";
import { formatApiDateTime } from "../../utils/flightUtils";
import PassengerDetails from "./PassengerDetails";
import ContactInfo from "./ContactInfo";
import SeatSelection from "./SeatSelection";
import SSROptions from "./SSROptions";

// Get segments from flight/leg object (same logic as RoundTripFlightCard)
const getSegmentsFromFlight = (leg) => {
  if (!leg) return [];
  if (Array.isArray(leg)) return leg.filter((s) => s && s.flightNo);
  if (leg?.Flights?.Onward) {
    const o = leg.Flights.Onward;
    return Array.isArray(o) ? o.filter((s) => s && s.flightNo) : Object.values(o).filter((s) => s && s.flightNo);
  }
  if (typeof leg === "object") {
    return Object.values(leg).filter((s) => s && s.flightNo);
  }
  return [];
};

// Format date/time for display (segment depDate/arrDate often YYYYMMDDHHmm from API)
const formatSegmentDateTime = (seg) => {
  if (!seg) return { date: "—", time: "—" };
  const depDate = seg.depDate || seg.departureDate || "";
  const arrDate = seg.arrDate || seg.arrivalDate || "";
  let dateStr = "—";
  let timeStr = "—";
  if (depDate && depDate.length >= 12) {
    dateStr = formatApiDateTime(depDate).split(",")[0]?.trim() || "—";
    timeStr = formatApiDateTime(depDate).split(",")[1]?.trim() || "—";
    if (arrDate && arrDate.length >= 12) {
      const arrTimePart = formatApiDateTime(arrDate).split(",")[1]?.trim();
      if (arrTimePart) timeStr = `${timeStr} – ${arrTimePart}`;
    }
  } else if (depDate) {
    try {
      const d = new Date(depDate);
      if (!isNaN(d.getTime())) {
        dateStr = d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
        timeStr = (seg.depTime || seg.departureTime || "") && (seg.arrTime || seg.arrivalTime || "")
          ? `${seg.depTime || seg.departureTime} – ${seg.arrTime || seg.arrivalTime}` : (seg.depTime || seg.arrTime || "—");
      }
    } catch (_) { }
  }
  return { date: dateStr, time: timeStr };
};

const PassengerForm = ({
  showPassengerForm,
  setShowPassengerForm,
  selectedFare,
  formData,
  formPassengerData,
  setFormPassengerData,
  contactInfo,
  setContactInfo,
  gstInfo,
  setGstInfo,
  showSeatSelection,
  setShowSeatSelection,
  availableSeats,
  setAvailableSeats,
  selectedSeats,
  setSelectedSeats,
  isLoadingSeats,
  selectedSsr,
  setSelectedSsr,
  webCheckinSelected,
  setWebCheckinSelected,
  isBooking,
  handleSubmit,
  register,
  errors,
  handlePassengerDataChange,
  handleDobChange,
  fetchSeats,
  selectedFlightForPrice,
  setSelectedFlightForPrice,
  statusRefdId,
  setIsOpen,
  bookingError,
  setBookingError,
  setAmount,
  setPriceSummary,
  selectedBaggagePerPax,
  setSelectedBaggagePerPax,
  selectedMealPerPax,
  setSelectedMealPerPax,
  ssrItems,
  setSsrItems,
}) => {
  const { currency } = useCurrency();
  const fare = selectedFlightForPrice || selectedFare;
  const paxCount = Math.max(1, (formData?.adults || 0) + (formData?.children || 0) + (formData?.infants || 0));
  if (!showPassengerForm || !selectedFare) return null;

  const isRoundTrip = fare?.isRoundTrip === true;
  // One-way: fare may be bookingData with nested selectedFare. Round trip: fare has selectedOnwardFare / selectedReturnFare.
  const onwardFare = fare?.selectedOnwardFare || fare?.selectedFare || selectedFare;
  const returnFare = fare?.selectedReturnFare;
  const requiresDocument = fare?.requiresDocument || selectedFare?.requiresDocument || fare?.doc_mandatory === 1 || selectedFare?.doc_mandatory === 1 ||
    onwardFare?.doc_mandatory === 1 || onwardFare?.priceCheckData?.doc_mandatory === 1 || returnFare?.doc_mandatory === 1 || returnFare?.priceCheckData?.doc_mandatory === 1;
  const requiresPAN = formData?.serType == 2 || fare?.requiresPAN || selectedFare?.requiresPAN || fare?.pan_mandatory === 1 || selectedFare?.pan_mandatory === 1 ||
    onwardFare?.pan_mandatory === 1 || onwardFare?.priceCheckData?.pan_mandatory === 1 || returnFare?.pan_mandatory === 1 || returnFare?.priceCheckData?.pan_mandatory === 1;
  const docTypeVal = fare?.document_type ?? selectedFare?.document_type ?? onwardFare?.document_type ?? onwardFare?.priceCheckData?.document_type ?? returnFare?.document_type;
  const documentType = fare?.documentType || selectedFare?.documentType || (docTypeVal === 1 ? "student" : docTypeVal === 2 ? "senior" : docTypeVal === 3 ? "defence" : "");

  // GST: show for all trip types (one-way, round trip domestic, international) when fare validation has gstInd 1 or 2
  const onwardGst = onwardFare?.validation?.gstInd ?? onwardFare?.originalFare?.validation?.gstInd;
  const returnGst = returnFare?.validation?.gstInd ?? returnFare?.originalFare?.validation?.gstInd;
  const gstInd = isRoundTrip
    ? (onwardGst === "1" || returnGst === "1" ? "1" : onwardGst === "2" || returnGst === "2" ? "2" : onwardGst === "0" && returnGst === "0" ? "0" : onwardGst || returnGst || "0")
    : (onwardFare?.validation?.gstInd ?? onwardFare?.originalFare?.validation?.gstInd ?? fare?.validation?.gstInd ?? selectedFare?.validation?.gstInd ?? selectedFare?.originalFare?.validation?.gstInd ?? "0");
  const showGstFields = gstInd === "1" || gstInd === "2";
  const gstMandatory = gstInd === "1";

  const totalAmount = isRoundTrip
    ? (fare?.totalPrice || (fare?.selectedOnwardFare?.price || 0) + (fare?.selectedReturnFare?.price || 0))
    : (fare?.totalPrice ?? fare?.price ?? selectedFare?.price ?? 0);
  const baseAmount = isRoundTrip
    ? (fare?.selectedOnwardFare?.basePrice || fare?.selectedOnwardFare?.price || 0) + (fare?.selectedReturnFare?.basePrice || fare?.selectedReturnFare?.price || 0)
    : (fare?.basePrice ?? fare?.price ?? selectedFare?.basePrice ?? selectedFare?.price ?? 0);
  const hasPerPaxSSR = Array.isArray(fare?.selectedBaggagePerPax) || Array.isArray(fare?.selectedMealPerPax);
  const ssrAmount = hasPerPaxSSR
    ? (fare?.selectedBaggagePerPax || []).reduce((s, b) => s + Number(b?.baggAmt || 0), 0) +
    (fare?.selectedMealPerPax || []).reduce((s, m) => s + Number(m?.mealAmt || 0), 0)
    : (fare?.selectedOnwardFare?.selectedBaggage?.baggAmt ? parseInt(fare.selectedOnwardFare.selectedBaggage.baggAmt, 10) : 0) +
    (fare?.selectedOnwardFare?.selectedMeal?.mealAmt ? parseFloat(fare.selectedOnwardFare.selectedMeal.mealAmt) : 0) +
    (fare?.selectedOnwardFare?.ssrInfo?.baggageOption?.baggAmt ? parseInt(fare.selectedOnwardFare.ssrInfo.baggageOption.baggAmt, 10) : 0) +
    (fare?.selectedOnwardFare?.ssrInfo?.mealOption?.mealAmt ? parseFloat(fare.selectedOnwardFare.ssrInfo.mealOption.mealAmt) : 0) +
    (fare?.selectedReturnFare?.selectedBaggage?.baggAmt ? parseInt(fare.selectedReturnFare.selectedBaggage.baggAmt, 10) : 0) +
    (fare?.selectedReturnFare?.selectedMeal?.mealAmt ? parseFloat(fare.selectedReturnFare.selectedMeal.mealAmt) : 0) +
    (fare?.selectedReturnFare?.ssrInfo?.baggageOption?.baggAmt ? parseInt(fare.selectedReturnFare.ssrInfo.baggageOption.baggAmt, 10) : 0) +
    (fare?.selectedReturnFare?.ssrInfo?.mealOption?.mealAmt ? parseFloat(fare.selectedReturnFare.ssrInfo.mealOption.mealAmt) : 0) +
    (fare?.ssrItems?.onward || []).reduce((s, i) => s + Number(i?.baggAmt || i?.mealAmt || 0), 0) +
    (fare?.ssrItems?.return || []).reduce((s, i) => s + Number(i?.baggAmt || i?.mealAmt || 0), 0) +
    (fare?.selectedBaggage?.baggAmt ? parseInt(fare.selectedBaggage.baggAmt, 10) : 0) +
    (fare?.selectedMeal?.mealAmt ? parseFloat(fare.selectedMeal.mealAmt) : 0) +
    (selectedFare?.selectedBaggage?.baggAmt ? parseInt(selectedFare.selectedBaggage.baggAmt, 10) : 0) +
    (selectedFare?.selectedMeal?.mealAmt ? parseFloat(selectedFare.selectedMeal.mealAmt) : 0);

  // Segment/route data for flight details cards (like big booking sites)
  const onwardSegments = getSegmentsFromFlight(fare?.onwardFlight);
  const returnSegments = getSegmentsFromFlight(fare?.returnFlight);
  const oneWaySegments = getSegmentsFromFlight(fare?.flight);
  const numberOfSectors = (fare?.isRoundTrip ? (onwardSegments.length + returnSegments.length) : (oneWaySegments.length || onwardSegments.length || 1));

  const isWebCheckinEnabled =
    onwardFare?.ssrInfo?.web_checkin_enabled === 1 ||
    fare?.ssrInfo?.web_checkin_enabled === 1 ||
    selectedFare?.ssrInfo?.web_checkin_enabled === 1 ||
    fare?.selectedOnwardFare?.ssrInfo?.web_checkin_enabled === 1;

  const baseWebCheckinCost =
    onwardFare?.ssrInfo?.web_checkin_amount ||
    fare?.ssrInfo?.web_checkin_amount || 79;
  const webCheckinUnitCost = baseWebCheckinCost * 2;

  const webCheckinTotalCost = webCheckinSelected ? (paxCount * numberOfSectors * webCheckinUnitCost) : 0;

  const seatTotalCost = Object.values(selectedSeats || {}).reduce((sum, s) => sum + parseInt(s?.seatAmt || 0, 10), 0);
  const finalTotalAmount = totalAmount + webCheckinTotalCost + ssrAmount + seatTotalCost;

  const onwardFirst = onwardSegments[0] || {};
  const onwardLast = onwardSegments[onwardSegments.length - 1] || {};
  const returnFirst = returnSegments[0] || {};
  const returnLast = returnSegments[returnSegments.length - 1] || {};
  const oneWayFirst = oneWaySegments[0] || {};
  const oneWayLast = oneWaySegments[oneWaySegments.length - 1] || {};
  const onwardAirlineName = onwardFirst?.airName || fare?.onwardFlight?.Flights?.AirlineName || "Flight";
  const returnAirlineName = returnFirst?.airName || fare?.returnFlight?.Flights?.AirlineName || "Flight";
  const oneWayAirlineName = fare?.airlineName || oneWayFirst?.airName || fare?.flight?.Flights?.AirlineName || "Flight";
  const onwardDateTime = formatSegmentDateTime(onwardFirst);
  const returnDateTime = formatSegmentDateTime(returnFirst);
  const oneWayDateTime = formatSegmentDateTime(oneWayFirst);

  // Book seats: build selectedFlight for /api/seats (refID + flightID per docs)
  const selectedFlight = {
    flightID: fare?.flightID || fare?.Flights?.Onward?.[0]?.flightID,
    refID: fare?.onwardRefID || statusRefdId,
  };
  const onProceedToPay = () => {
    if (gstMandatory && (!(gstInfo?.gstNo ?? "").trim() || !(gstInfo?.companyName ?? "").trim())) {
      alert("GSTIN and Company Name are required for this fare.");
      return;
    }
    if (webCheckinSelected) {
      const seatPaxCount = (formData?.adults || 1) + (formData?.children || 0); // Infants don't get seats
      const numLegs = fare?.isRoundTrip ? 2 : 1;
      const requiredSeats = seatPaxCount * numLegs;
      const selectedSeatsCount = Object.keys(selectedSeats || {}).length;
      if (selectedSeatsCount < requiredSeats) {
        alert(`Seat Selection is mandatory when Auto Web Check-in is enabled. Please select seats for all passengers (${requiredSeats} seats required, ${selectedSeatsCount} selected).`);
        return;
      }
    }
    setIsOpen(true);
  };

  // Sync finalTotalAmount back to Homepage2 so booking payload has correct amount
  useEffect(() => {
    if (setAmount) setAmount(finalTotalAmount);
    if (setPriceSummary) setPriceSummary({
      baseFare: totalAmount,
      ssrAddons: ssrAmount,
      webCheckin: webCheckinTotalCost,
      seatCharges: seatTotalCost,
      total: finalTotalAmount,
    });
  }, [finalTotalAmount]);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 py-3 sm:py-4 md:py-6 px-3 sm:px-4 md:px-6 overflow-y-auto">
      <motion.div
        className="bg-white w-full max-w-7xl rounded-2xl flex flex-col shadow-2xl my-auto border border-gray-200/60"
        style={{ maxHeight: "calc(100vh - 1.5rem)" }}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Header – sticky at top so title + close always visible on scroll */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-5 lg:px-6 py-3 sm:py-3.5 border-b border-gray-200 bg-white shadow-sm shrink-0 rounded-t-2xl">
          <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 tracking-tight">
            Passenger Details
          </h2>
          <button
            onClick={() => {
              setShowPassengerForm(false);
              setBookingError && setBookingError(null);
            }}
            className="text-gray-400 hover:text-gray-700 text-lg sm:text-xl p-1.5 sm:p-2 rounded-full hover:bg-gray-100 transition-colors"
            type="button"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Booking error banner – e.g. Flight session expired, PAN required */}
        {bookingError && (
          <div className="mx-4 sm:mx-5 lg:mx-6 mt-3 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <FaInfoCircle className="text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-800">{bookingError}</p>
              {bookingError.toLowerCase().includes("session expired") || bookingError.toLowerCase().includes("search again") ? (
                <p className="text-xs text-red-700 mt-1">Please close this form, search for flights again, and complete your booking within a few minutes.</p>
              ) : null}
            </div>
            <button
              onClick={() => setBookingError && setBookingError(null)}
              className="shrink-0 text-red-600 hover:text-red-800 text-sm font-medium"
              type="button"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-hidden">
          {/* LEFT: Flight details – Onward & Return cards (desktop: fixed column) */}
          <div className="lg:w-[360px] xl:w-[400px] border-b lg:border-b-0 lg:border-r border-gray-200 bg-gradient-to-br from-slate-50/90 to-white lg:overflow-y-auto shrink-0 lg:min-h-0">
            <div className="p-4 lg:p-5 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:block">Your booking</p>
              {isRoundTrip ? (
                <>
                  {/* Onward Flight – clear card */}
                  <div className="rounded-xl border border-blue-200/80 bg-white shadow-md overflow-hidden">
                    <div className="px-4 py-2.5 bg-blue-600">
                      <span className="text-xs font-bold text-white uppercase tracking-wide flex items-center gap-1.5">
                        <FaPlane className="text-white opacity-90" /> Onward Flight
                      </span>
                    </div>
                    <div className="p-4 space-y-2.5">
                      <div className="flex items-center gap-2 text-gray-800">
                        <span className="text-lg font-bold">{onwardFirst?.depCode || "—"}</span>
                        <FaExchangeAlt className="text-blue-500 text-sm shrink-0" />
                        <span className="text-lg font-bold">{onwardLast?.arrCode || "—"}</span>
                      </div>
                      <p className="text-xs text-gray-600">{onwardAirlineName} • {onwardFirst?.flightNo || "—"}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FaClock className="text-gray-400 shrink-0" />
                        <span>{onwardDateTime.date}</span>
                        <span className="text-gray-400">|</span>
                        <span>{onwardDateTime.time}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-100 flex justify-between items-baseline">
                        <span className="text-xs text-gray-600">{onwardFare?.name || onwardFare?.fareTypeDesc || "Fare"}</span>
                        <span className="text-base font-bold text-blue-600">{formatPriceSync(onwardFare?.price ?? (!returnFare ? fare?.totalPrice : 0) ?? 0, currency)}</span>
                      </div>
                      {(onwardFare?.ssrInfo?.baggageOption || onwardFare?.ssrInfo?.mealOption) && (
                        <p className="text-xs text-gray-500 pt-1">
                          + {onwardFare.ssrInfo.baggageOption?.baggDesc && `Baggage • `}
                          {onwardFare.ssrInfo.mealOption?.mealDesc && `Meal`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Return Flight – clear card */}
                  <div className="rounded-xl border border-green-200/80 bg-white shadow-md overflow-hidden">
                    <div className="px-4 py-2.5 bg-green-600">
                      <span className="text-xs font-bold text-white uppercase tracking-wide flex items-center gap-1.5">
                        <FaPlane className="text-white opacity-90" /> Return Flight
                      </span>
                    </div>
                    <div className="p-4 space-y-2.5">
                      <div className="flex items-center gap-2 text-gray-800">
                        <span className="text-lg font-bold">{returnFirst?.depCode || "—"}</span>
                        <FaExchangeAlt className="text-green-500 text-sm shrink-0" />
                        <span className="text-lg font-bold">{returnLast?.arrCode || "—"}</span>
                      </div>
                      <p className="text-xs text-gray-600">{returnAirlineName} • {returnFirst?.flightNo || "—"}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <FaClock className="text-gray-400 shrink-0" />
                        <span>{returnDateTime.date}</span>
                        <span className="text-gray-400">|</span>
                        <span>{returnDateTime.time}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-100 flex justify-between items-baseline">
                        <span className="text-xs text-gray-600">{returnFare?.name || returnFare?.fareTypeDesc || "Fare"}</span>
                        <span className="text-base font-bold text-green-600">{formatPriceSync(returnFare?.price ?? 0, currency)}</span>
                      </div>
                      {(returnFare?.ssrInfo?.baggageOption || returnFare?.ssrInfo?.mealOption) && (
                        <p className="text-xs text-gray-500 pt-1">
                          + {returnFare.ssrInfo.baggageOption?.baggDesc && `Baggage • `}
                          {returnFare.ssrInfo.mealOption?.mealDesc && `Meal`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Total + add-ons – prominent block */}
                  <div className="rounded-xl border-2 border-gray-200 bg-gray-50 p-4 shadow-sm">
                    <div className="flex justify-between items-baseline text-sm text-gray-700">
                      <span>Base fare</span>
                      <span className="font-semibold">{formatPriceSync((onwardFare?.price ?? (!returnFare ? fare?.totalPrice : 0) ?? 0) + (returnFare?.price ?? 0), currency)}</span>
                    </div>
                    {ssrAmount > 0 && (
                      <div className="flex justify-between items-baseline text-xs text-gray-600 mt-1.5">
                        <span>Add-ons</span>
                        <span>+{formatPriceSync(ssrAmount, currency)}</span>
                      </div>
                    )}
                    {Object.keys(selectedSeats || {}).length > 0 && (
                      <div className="flex justify-between items-baseline text-xs text-gray-600 mt-0.5">
                        <span>Seats</span>
                        <span>{seatTotalCost >= 0 ? '+' : ''}{formatPriceSync(seatTotalCost, currency)}</span>
                      </div>
                    )}
                    {webCheckinSelected && (
                      <div className="flex justify-between items-baseline text-xs text-gray-600 mt-0.5">
                        <span>Web Check-in</span>
                        <span>+{formatPriceSync(webCheckinTotalCost, currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline mt-3 pt-3 border-t-2 border-gray-200">
                      <span className="font-bold text-gray-900 text-base">Total</span>
                      <span className="text-xl font-bold text-gray-900">{formatPriceSync(finalTotalAmount, currency)}</span>
                    </div>
                  </div>
                </>
              ) : (
                /* One-way: single flight card */
                <div className="rounded-xl border border-blue-200/80 bg-white shadow-md overflow-hidden">
                  <div className="px-4 py-2.5 bg-blue-600">
                    <span className="text-xs font-bold text-white uppercase tracking-wide flex items-center gap-1.5">
                      <FaPlane className="text-white opacity-90" /> Flight details
                    </span>
                  </div>
                  <div className="p-4 space-y-2.5">
                    <div className="flex items-center gap-2 text-gray-800">
                      <span className="text-lg font-bold">{oneWayFirst?.depCode || "—"}</span>
                      <FaExchangeAlt className="text-blue-500 text-sm shrink-0" />
                      <span className="text-lg font-bold">{oneWayLast?.arrCode || "—"}</span>
                    </div>
                    <p className="text-xs text-gray-600">{oneWayAirlineName} • {oneWayFirst?.flightNo || "—"}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <FaClock className="text-gray-400 shrink-0" />
                      <span>{oneWayDateTime.date}</span>
                      <span className="text-gray-400">|</span>
                      <span>{oneWayDateTime.time}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100 flex justify-between items-baseline">
                      <span className="text-xs text-gray-600">{onwardFare?.name || onwardFare?.fareTypeDesc || selectedFare?.fareTypeDesc || "Fare"}</span>
                      <span className="text-base font-bold text-blue-600">{formatPriceSync(totalAmount, currency)}</span>
                    </div>
                    {ssrAmount > 0 && (
                      <div className="flex justify-between text-xs text-gray-600 pt-1">
                        <span>Add-ons</span>
                        <span>+{formatPriceSync(ssrAmount, currency)}</span>
                      </div>
                    )}
                    {Object.keys(selectedSeats || {}).length > 0 && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Seats</span>
                        <span>{seatTotalCost >= 0 ? '+' : ''}{formatPriceSync(seatTotalCost, currency)}</span>
                      </div>
                    )}
                    {webCheckinSelected && (
                      <div className="flex justify-between text-xs text-gray-600">
                        <span>Web Check-in</span>
                        <span>+{formatPriceSync(webCheckinTotalCost, currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-baseline pt-2 mt-2 border-t border-gray-100">
                      <span className="font-bold text-gray-900 text-sm">Total</span>
                      <span className="text-lg font-bold text-gray-900">{formatPriceSync(finalTotalAmount, currency)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation – GST / FQT */}
              {(showGstFields || onwardFare?.validation?.allowFQT !== undefined || onwardFare?.originalFare?.validation?.allowFQT !== undefined) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/90 overflow-hidden shadow-sm">
                  <div className="px-3 py-2.5 border-b border-slate-200 bg-slate-100/50">
                    <h3 className="text-xs font-bold text-slate-700 flex items-center gap-2">
                      <FaFileInvoiceDollar className="text-slate-500 text-sm" />
                      Validation
                    </h3>
                  </div>
                  <ul className="px-3 py-2.5 text-xs text-slate-700 space-y-1.5">
                    {showGstFields && (
                      <li className={gstMandatory ? "text-amber-700 font-medium" : "text-blue-700"}>
                        GST – {gstMandatory ? "Mandatory" : "Optional"}
                      </li>
                    )}
                    {(onwardFare?.validation?.allowFQT ?? onwardFare?.originalFare?.validation?.allowFQT) !== undefined && (
                      <li className="text-slate-600">
                        FQT – {(onwardFare?.validation?.allowFQT ?? onwardFare?.originalFare?.validation?.allowFQT) === "1" ? "Allowed" : "Not Allowed"}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Doc / PAN hints */}
              {(requiresDocument || requiresPAN || documentType) && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/90 overflow-hidden shadow-sm">
                  <div className="px-3 py-2.5 border-b border-amber-200 bg-amber-100/50">
                    <h3 className="text-xs font-bold text-amber-800 flex items-center gap-2">
                      <FaInfoCircle className="text-amber-600" />
                      Required for this fare
                    </h3>
                  </div>
                  <ul className="px-3 py-2.5 text-xs text-amber-800 space-y-0.5">
                    {requiresDocument && (
                      <li>• ID proof {documentType && `(${documentType})`}</li>
                    )}
                    {requiresPAN && <li>• PAN (first passenger) – mandatory for International flights</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Form – wider, scrollable, clear sections */}
          <form onSubmit={handleSubmit(onProceedToPay)} className="flex-1 flex flex-col min-h-0 lg:overflow-hidden min-w-0 bg-gray-50/30">
            <div className="flex-1 lg:overflow-y-auto p-4 sm:p-5 lg:p-6 space-y-4 sm:space-y-6">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider lg:hidden">Passenger & GST</p>
              {/* GST fields – conditional */}
              {showGstFields && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <FaFileInvoiceDollar className="text-slate-600" />
                    GST details {gstMandatory && <span className="text-amber-600 text-xs font-medium">(required)</span>}
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-1">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">GSTIN</label>
                      <input
                        type="text"
                        placeholder="e.g. 22AAAAA0000A1Z5"
                        value={gstInfo?.gstNo ?? ""}
                        onChange={(e) => setGstInfo((prev) => ({ ...prev, gstNo: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Company name</label>
                      <input
                        type="text"
                        placeholder="Legal name of business"
                        value={gstInfo?.companyName ?? ""}
                        onChange={(e) => setGstInfo((prev) => ({ ...prev, companyName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                      <input
                        type="text"
                        placeholder="e.g. Meri Gali, 7th Block, Bangalore - 560010"
                        value={gstInfo?.address ?? ""}
                        onChange={(e) => setGstInfo((prev) => ({ ...prev, address: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                      />
                    </div>
                  </div>
                </div>
              )}

              <PassengerDetails
                formData={{
                  ...formData,
                  requiresDocument: !!requiresDocument,
                  requiresPAN: !!requiresPAN,
                  documentType: documentType || "",
                }}
                formPassengerData={formPassengerData}
                handlePassengerDataChange={handlePassengerDataChange}
                handleDobChange={handleDobChange}
                register={register}
                errors={errors}
              />

              <SSROptions
                selectedSsr={selectedSsr}
                setSelectedSsr={setSelectedSsr}
                setShowSeatSelection={setShowSeatSelection}
                selectedFare={fare}
              />

              {isWebCheckinEnabled && (
                <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 sm:p-5 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <input
                        type="checkbox"
                        id="webCheckinToggle"
                        checked={webCheckinSelected}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setWebCheckinSelected(isChecked);
                          // Auto-select web check-in removed to allow user choice
                          if (isChecked) {
                            alert("Web Check-in is enabled! SSR booking and Seat Selection are now mandatory. Please select your seats from the Add-ons section below.");
                            setSelectedSsr({ type: "with" });
                            setShowSeatSelection(true);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="webCheckinToggle" className="text-sm font-bold text-gray-900 cursor-pointer flex items-center justify-between">
                        <span>Add Auto Web Check-in</span>
                        <span className="text-blue-600 font-bold">+{formatPriceSync(paxCount * numberOfSectors * webCheckinUnitCost, currency)}</span>
                      </label>
                      <p className="text-xs text-gray-600 mt-1">
                        Let us do the web check-in for you and send the boarding pass directly to your email/whatsapp. (Cost calculated as {paxCount} pax × {numberOfSectors} sector(s) × {webCheckinUnitCost})
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Add-ons (baggage / meal) – when user chose Include add-ons (SSR): show selected add-ons (one-way + round trip) */}
              {selectedSsr?.type === "with" && (() => {
                const hasOneWayOptions = fare?.baggageOptions?.length > 0 || fare?.mealOptions?.length > 0;
                const hasRoundTripOptions =
                  fare?.selectedOnwardFare?.baggageOptions?.length > 0 ||
                  fare?.selectedOnwardFare?.mealOptions?.length > 0 ||
                  fare?.selectedReturnFare?.baggageOptions?.length > 0 ||
                  fare?.selectedReturnFare?.mealOptions?.length > 0;
                const hasRoundTripSelected =
                  fare?.selectedOnwardFare?.ssrInfo?.baggageOption ||
                  fare?.selectedOnwardFare?.ssrInfo?.mealOption ||
                  fare?.selectedReturnFare?.ssrInfo?.baggageOption ||
                  fare?.selectedReturnFare?.ssrInfo?.mealOption;
                const hasInternationalSSR =
                  (Array.isArray(fare?.ssrItems?.onward) && fare.ssrItems.onward.length > 0) ||
                  (Array.isArray(fare?.ssrItems?.return) && fare.ssrItems.return.length > 0);
                const showSection = hasOneWayOptions || hasRoundTripOptions || hasRoundTripSelected || hasInternationalSSR;
                if (!showSection || !setSelectedFlightForPrice) return null;

                return (
                  <div className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <FaSuitcase className="text-slate-600" />
                      Add-ons (Baggage & Meals)
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {paxCount > 1 ? "Select baggage and meal for each passenger." : "Optional add-ons – pay only if selected."}
                    </p>

                    {/* Round trip (domestic / international): show selected add-ons summary when user already selected on fare card */}
                    {(hasRoundTripSelected || hasInternationalSSR) && (
                      <div className="mb-4 p-3 rounded-lg border border-green-200 bg-green-50/80">
                        <p className="text-sm font-medium text-gray-800 mb-2">Selected add-ons (included in this booking)</p>
                        {hasRoundTripSelected && (
                          <div className="space-y-2 text-sm text-gray-700">
                            {(fare?.selectedOnwardFare?.ssrInfo?.baggageOption || fare?.selectedOnwardFare?.ssrInfo?.mealOption) && (
                              <div>
                                <span className="font-medium text-blue-700">Onward:</span>{" "}
                                {fare.selectedOnwardFare.ssrInfo.baggageOption && (
                                  <span>{fare.selectedOnwardFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(parseInt(fare.selectedOnwardFare.ssrInfo.baggageOption.baggAmt || 0), currency)})</span>
                                )}
                                {fare.selectedOnwardFare.ssrInfo.baggageOption && fare.selectedOnwardFare.ssrInfo.mealOption && " • "}
                                {fare.selectedOnwardFare.ssrInfo.mealOption && (
                                  <span>{fare.selectedOnwardFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(parseFloat(fare.selectedOnwardFare.ssrInfo.mealOption.mealAmt || 0), currency)})</span>
                                )}
                              </div>
                            )}
                            {(fare?.selectedReturnFare?.ssrInfo?.baggageOption || fare?.selectedReturnFare?.ssrInfo?.mealOption) && (
                              <div>
                                <span className="font-medium text-green-700">Return:</span>{" "}
                                {fare.selectedReturnFare.ssrInfo.baggageOption && (
                                  <span>{fare.selectedReturnFare.ssrInfo.baggageOption.baggDesc} (+{formatPriceSync(parseInt(fare.selectedReturnFare.ssrInfo.baggageOption.baggAmt || 0), currency)})</span>
                                )}
                                {fare.selectedReturnFare.ssrInfo.baggageOption && fare.selectedReturnFare.ssrInfo.mealOption && " • "}
                                {fare.selectedReturnFare.ssrInfo.mealOption && (
                                  <span>{fare.selectedReturnFare.ssrInfo.mealOption.mealDesc} (+{formatPriceSync(parseFloat(fare.selectedReturnFare.ssrInfo.mealOption.mealAmt || 0), currency)})</span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {hasInternationalSSR && !hasRoundTripSelected && (
                          <div className="space-y-2 text-sm text-gray-700">
                            {fare?.ssrItems?.onward?.length > 0 && (
                              <div>
                                <span className="font-medium text-blue-700">Onward:</span>{" "}
                                {fare.ssrItems.onward.map((item, i) => (
                                  <span key={i}>
                                    {i > 0 && " • "}
                                    {item.baggDesc || item.mealDesc} (+{formatPriceSync(parseFloat(item.baggAmt || item.mealAmt || 0), currency)})
                                  </span>
                                ))}
                              </div>
                            )}
                            {fare?.ssrItems?.return?.length > 0 && (
                              <div>
                                <span className="font-medium text-green-700">Return:</span>{" "}
                                {fare.ssrItems.return.map((item, i) => (
                                  <span key={i}>
                                    {i > 0 && " • "}
                                    {item.baggDesc || item.mealDesc} (+{formatPriceSync(parseFloat(item.baggAmt || item.mealAmt || 0), currency)})
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* One-way: show dropdowns to select/change add-ons */}
                    {hasOneWayOptions && (
                      <div className="space-y-4">
                        {paxCount > 1 ? (
                          Array.from({ length: paxCount }, (_, paxIdx) => (
                            <div key={paxIdx} className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 space-y-3">
                              <h4 className="text-sm font-semibold text-gray-800">Passenger {paxIdx + 1}</h4>
                              {fare.baggageOptions?.length > 0 && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <FaSuitcase className="inline mr-1" /> Baggage
                                  </label>
                                  <select
                                    className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={fare?.selectedBaggagePerPax?.[paxIdx]?.baggID ?? ""}
                                    onChange={(e) => {
                                      const next = [...(fare?.selectedBaggagePerPax || Array(paxCount).fill(null))];
                                      if (e.target.value === "") {
                                        next[paxIdx] = null;
                                      } else {
                                        const bag = fare.baggageOptions.find((b) => b.baggID === e.target.value);
                                        if (bag) next[paxIdx] = bag;
                                      }
                                      setSelectedFlightForPrice((prev) => ({ ...prev, selectedBaggagePerPax: next }));
                                    }}
                                  >
                                    <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                                    {fare.baggageOptions.map((bag) => (
                                      <option key={bag.baggID} value={bag.baggID}>
                                        {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              {fare.mealOptions?.length > 0 && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    <FaUtensils className="inline mr-1" /> Meal
                                  </label>
                                  <select
                                    className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                    value={fare?.selectedMealPerPax?.[paxIdx]?.mealID ?? ""}
                                    onChange={(e) => {
                                      const next = [...(fare?.selectedMealPerPax || Array(paxCount).fill(null))];
                                      if (e.target.value === "") {
                                        next[paxIdx] = null;
                                      } else {
                                        const meal = fare.mealOptions.find((m) => m.mealID === e.target.value);
                                        if (meal) next[paxIdx] = meal;
                                      }
                                      setSelectedFlightForPrice((prev) => ({ ...prev, selectedMealPerPax: next }));
                                    }}
                                  >
                                    <option value="">No meal ({formatPriceSync(0, currency)})</option>
                                    {fare.mealOptions.map((meal) => (
                                      <option key={meal.mealID} value={meal.mealID}>
                                        {meal.mealDesc} {meal.mealRef && `(Seg ${meal.mealRef})`} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <>
                            {fare.baggageOptions?.length > 0 && (
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                  <FaSuitcase className="inline mr-1 text-gray-600" /> Extra Baggage
                                </label>
                                <select
                                  className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                  value={fare?.selectedBaggage?.baggID ?? ""}
                                  onChange={(e) => {
                                    if (e.target.value === "") {
                                      setSelectedFlightForPrice((prev) => ({ ...prev, selectedBaggage: null }));
                                    } else {
                                      const bag = fare.baggageOptions.find((b) => b.baggID === e.target.value);
                                      if (bag) setSelectedFlightForPrice((prev) => ({ ...prev, selectedBaggage: bag }));
                                    }
                                  }}
                                >
                                  <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                                  {fare.baggageOptions.map((bag) => (
                                    <option key={bag.baggID} value={bag.baggID}>
                                      {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                                    </option>
                                  ))}
                                </select>
                                {fare?.selectedBaggage && (
                                  <p className="mt-1 text-xs text-green-600">
                                    ✓ {fare.selectedBaggage.baggDesc} (+{formatPriceSync(parseInt(fare.selectedBaggage.baggAmt || 0), currency)})
                                  </p>
                                )}
                              </div>
                            )}
                            {fare.mealOptions?.length > 0 && (
                              <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                  <FaUtensils className="inline mr-1 text-gray-600" /> Meals
                                </label>
                                <select
                                  className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                  value={fare?.selectedMeal?.mealID ?? ""}
                                  onChange={(e) => {
                                    if (e.target.value === "") {
                                      setSelectedFlightForPrice((prev) => ({ ...prev, selectedMeal: null }));
                                    } else {
                                      const meal = fare.mealOptions.find((m) => m.mealID === e.target.value);
                                      if (meal) setSelectedFlightForPrice((prev) => ({ ...prev, selectedMeal: meal }));
                                    }
                                  }}
                                >
                                  <option value="">No meal ({formatPriceSync(0, currency)})</option>
                                  {fare.mealOptions.map((meal) => (
                                    <option key={meal.mealID} value={meal.mealID}>
                                      {meal.mealDesc} {meal.mealRef && `(Seg ${meal.mealRef})`} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                                    </option>
                                  ))}
                                </select>
                                {fare?.selectedMeal && (
                                  <p className="mt-1 text-xs text-green-600">
                                    ✓ {fare.selectedMeal.mealDesc} (+{formatPriceSync(parseFloat(fare.selectedMeal.mealAmt || 0), currency)})
                                  </p>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              <SeatSelection
                direction="Onward"
                showSeatSelection={showSeatSelection}
                availableSeats={availableSeats}
                selectedSeats={selectedSeats}
                setSelectedSeats={setSelectedSeats}
                isLoadingSeats={isLoadingSeats}
                fetchSeats={fetchSeats}
                selectedFlight={selectedFlight}
                formData={formData}
              />

              <ContactInfo
                contactInfo={contactInfo}
                setContactInfo={setContactInfo}
                register={register}
                errors={errors}
                setValue={() => { }}
              />
            </div>

            <div className="sticky bottom-0 shrink-0 border-t border-gray-200 bg-white px-4 sm:px-5 lg:px-6 py-3 sm:py-4 shadow-[0_-2px_10px_rgba(0,0,0,0.04)] z-10">
              <ActionButtons
                setShowPassengerForm={setShowPassengerForm}
                isBooking={isBooking}
              />
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

const ActionButtons = ({ setShowPassengerForm, isBooking }) => (
  <div className="flex flex-col sm:flex-row flex-wrap justify-end gap-2 sm:gap-3">
    <button
      type="button"
      onClick={() => setShowPassengerForm(false)}
      className="w-full sm:w-auto px-4 sm:px-5 py-2.5 min-h-[44px] border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-gray-700 transition-colors text-sm sm:text-base"
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={isBooking}
      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 min-h-[44px] bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
    >
      {isBooking ? "Booking..." : "Proceed to Pay"}
    </button>
  </div>
);

export default PassengerForm;
